import "regenerator-runtime/runtime";

// Redux Store Configuration
import { applyMiddleware, createStore } from "redux";
import { persistReducer, persistStore, createTransform } from "redux-persist";

import createSagaMiddleware from "redux-saga";
import rootReducer from "../reducers/index";
import rootSaga from "../sagas/index";
import localStorage from "redux-persist/lib/storage";
import sessionStorage from "redux-persist/lib/storage/session";
import { parseURLQueryParams } from "../../utils/utils";
import storageService from "../../services/storageService";
import { composeWithDevTools } from "redux-devtools-extension";
import { devLog } from "../../utils/devLog";

export const STORAGE_TYPE = "STORAGE_TYPE";
// create the saga middleware
const sagaMiddleware = createSagaMiddleware();

const middleware = applyMiddleware(sagaMiddleware);

let query = parseURLQueryParams(window.location.search);

const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
              window.navigator.standalone === true;

let storageType;
if (isPWA) {
  storageType = "local";
} else if (query && query.store) {
  storageType = query.store;
} else {
  storageType = null;
}

let storage;
if (isPWA) {
  storage = localStorage;
  storageService.addToLocalStorage(STORAGE_TYPE, "local");
} else {
  storage = storageType === "local" ? localStorage : sessionStorage;
}

// Transform to limit the size of patient data to avoid localStorage quota issues
const patientDataLimiter = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState, key) => {
    // Skip if not dealing with patients reducer
    if (key !== 'patients') return inboundState;
    
    // Create a trimmed copy of the state
    const trimmedState = { ...inboundState };
    
    // Keep only essential patient data, limit list size
    if (trimmedState.list && trimmedState.list.length) {
      // Limit to at most 50 patients
      trimmedState.list = trimmedState.list.slice(0, 50).map(patient => {
        // Create a minimized version of each patient object
        if (!patient) return null;
        
        // Return a trimmed patient object with just essential fields
        return {
          id: patient.id,
          userId: patient.userId,
          patientId: patient.patientId,
          patientName: patient.patientName,
          person: patient.person ? {
            id: patient.person.id,
            name: patient.person.name
          } : null,
          patientType: patient.patientType,
          patientTypeText: patient.patientTypeText,
          patientTypeId: patient.patientTypeId,
          orgId: patient.orgId
        };
      }).filter(Boolean);
    }
    
    return trimmedState;
  },
  // Transform state being rehydrated (no transformation needed)
  (outboundState) => outboundState,
  // Only apply to the patients reducer
  { whitelist: ['patients'] }
);

// Transform to limit surveys data size
const surveysLimiter = createTransform(
  (inboundState) => {
    // Only store minimal survey metadata, not the full survey data
    if (inboundState && inboundState.list) {
      return {
        ...inboundState,
        list: inboundState.list.slice(0, 20).map(survey => ({
          id: survey.id,
          title: survey.title,
          description: survey.description,
          createdAt: survey.createdAt,
          updatedAt: survey.updatedAt
        }))
      };
    }
    return inboundState;
  },
  (outboundState) => outboundState,
  { whitelist: ['surveys'] }
);

// Flag to enable/disable the user state limiter (for testing purposes)
let userStateLimiterEnabled = true;

// Transform to strip large fields from user state before persistence
const userStateLimiter = createTransform(
  (inboundState) => {
    // Allow disabling this transform for testing purposes
    if (!userStateLimiterEnabled) {
      return inboundState;
    }

    // Strip out attributeUIFields from user.data as it's stored separately in global storage
    // This field can be 2.5MB+ and causes the user reducer to exceed the 2MB persistence limit
    if (inboundState && inboundState.data && inboundState.data.attributeUIFields) {
      const { attributeUIFields, ...dataWithoutUIFields } = inboundState.data;
      return {
        ...inboundState,
        data: dataWithoutUIFields
      };
    }
    return inboundState;
  },
  (outboundState) => outboundState,
  { whitelist: ['user'] }
);

// Transform to safely persist global state with size limits
const globalStateLimiter = createTransform(
  (inboundState) => {
    // Safety check: if attributeUIFields is too large (>2MB), don't persist it
    // This prevents storage quota issues while allowing smaller field definitions to persist
    if (inboundState && inboundState.attributeUIFields) {
      try {
        const size = JSON.stringify(inboundState.attributeUIFields).length;
        const sizeInMB = size / (1024 * 1024);

        if (sizeInMB > 2) {
          console.warn(`Global attributeUIFields too large (${sizeInMB.toFixed(2)}MB), not persisting. Will be fetched from API on next load.`);
          return {
            ...inboundState,
            attributeUIFields: undefined
          };
        }
      } catch (error) {
        console.error('Error checking global state size:', error);
        return { ...inboundState, attributeUIFields: undefined };
      }
    }
    return inboundState;
  },
  (outboundState) => outboundState,
  { whitelist: ['global'] }
);

// Transform to check size before serializing and abort if too large
const sizeLimiter = createTransform(
  (inboundState, key) => {
    try {
      // Rough size estimation
      const stateString = JSON.stringify(inboundState);
      const sizeInMB = stateString.length / (1024 * 1024);

      // If a single reducer's state is too large, don't persist it
      // Fresh data will be fetched from API on next page load
      if (sizeInMB > 2) {
        devLog(`⚠️  Redux state for "${key}" is too large (${sizeInMB.toFixed(2)}MB). Not persisting. Data will be fetched fresh on next page load.`);

        // CRITICAL: For patients reducer, preserve filter states even if we can't persist the lists
        // This prevents losing advanced search filters when patient lists are large
        if (key === 'patients') {
          devLog(`🔧 Preserving filter states for patients reducer despite size limit`);
          return {
            allFilter: inboundState.allFilter,
            watchlistFilter: inboundState.watchlistFilter,
            dashboardFilter: inboundState.dashboardFilter,
            savedQueryFilters: inboundState.savedQueryFilters,
            savedSearches: inboundState.savedSearches,
            // Reset list states to initial - they'll be fetched fresh
            all: { list: [], loading: false, page: 0, loadMore: true, total: undefined, activePatientId: null },
            watchlist: { list: [], loading: false, page: 0, loadMore: true, total: undefined, activePatientId: null },
            dashboard: { list: [], loading: false, page: 0, loadMore: true, total: undefined, activePatientId: null },
            savedQuery: {},
            mode: inboundState.mode || 'all',
            fresh: true,
          };
        }

        return undefined; // Don't persist this state - will fetch fresh from API
      }
    } catch (error) {
      devLog('Error checking state size:', error);
      return undefined; // Don't persist on error
    }

    return inboundState;
  },
  (outboundState) => outboundState
);

const persistConfig = {
  key: "root",
  blacklist: [
    "global", // Keep global blacklisted - attributeUIFields are fetched fresh from API on each session
    "members",
    "organizations",
    "organizationAdmins",
    "staff",
    "statuses",
    "patientTypes",
    "toaster",
    "uploadProgress",
    "masterData",
    "infoPanel",
    "carePlan",
    "contentLibrary",
    "clinics"
  ],
  transforms: [
    patientDataLimiter,
    surveysLimiter,
    userStateLimiter,
    sizeLimiter
  ],
  storage
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const initialState = {};

export const store = createStore(
  persistedReducer,
  initialState,
  composeWithDevTools(middleware),
);

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);

// Function to get a rough size estimate of the Redux state
const getStateSize = (state) => {
  try {
    const jsonState = JSON.stringify(state);
    return {
      bytes: jsonState.length,
      kb: Math.round(jsonState.length / 1024),
      mb: (jsonState.length / (1024 * 1024)).toFixed(2)
    };
  } catch (error) {
    devLog('Error calculating state size:', error);
    return { bytes: 0, kb: 0, mb: 0 };
  }
};

// Monitor state size and purge if necessary to prevent quota issues
store.subscribe(() => {
  // Only check every 10 seconds to avoid performance impact
  if (!store._lastSizeCheck || (Date.now() - store._lastSizeCheck > 10000)) {
    store._lastSizeCheck = Date.now();
    
    const state = store.getState();
    const size = getStateSize(state);
    
    // If state is getting too large, purge persisted state
    // Fresh data will be fetched from API on next page load
    if (size.mb > 4) {
      devLog(`Redux state is too large (${size.mb}MB), purging persistence to prevent quota issues. Data will be fetched fresh on next page load.`);
      persistor.purge();
    }
  }
});

// Expose utility functions to debug and fix storage quota issues
if (typeof window !== 'undefined') {
  // Add debugging utils to window object for console access
  window.__mpsPurgeReduxStore = () => {
    persistor.purge();
    devLog('Redux persistence has been purged');
    return 'Redux store purged successfully. Refresh the page to see the effect.';
  };

  // Toggle the user state limiter fix on/off for testing
  window.__mpsDisableUserStateFix = () => {
    userStateLimiterEnabled = false;
    devLog('❌ User state fix DISABLED - attributeUIFields will be persisted (simulates the bug)');
    devLog('⚠️  This will cause logout on second refresh if large data is present!');
    return 'Fix disabled. Use window.__mpsEnableUserStateFix() to re-enable.';
  };

  window.__mpsEnableUserStateFix = () => {
    userStateLimiterEnabled = true;
    devLog('✅ User state fix ENABLED - attributeUIFields will be stripped from persistence');
    return 'Fix enabled.';
  };

  window.__mpsCheckFixStatus = () => {
    devLog(`User state fix is currently: ${userStateLimiterEnabled ? '✅ ENABLED' : '❌ DISABLED'}`);
    return userStateLimiterEnabled;
  };
  
  window.__mpsCheckStoreSize = () => {
    const state = store.getState();
    const size = getStateSize(state);
    devLog(`Redux store size: ${size.mb}MB (${size.kb}KB)`);

    // Show individual reducer sizes
    const reducerSizes = {};
    Object.keys(state).forEach(key => {
      const reducerSize = getStateSize(state[key]);
      reducerSizes[key] = `${reducerSize.mb}MB`;
      devLog(`- ${key}: ${reducerSize.mb}MB`);
    });

    return {
      totalSize: `${size.mb}MB`,
      reducers: reducerSizes,
      message: size.mb > 3 ? 'Store is large, consider purging' : 'Store size is acceptable'
    };
  };

  // Test helper to simulate the large user state bug
  window.__mpsSimulateLargeUserState = () => {
    devLog('🧪 Simulating large user state by adding fake attributeUIFields...');

    // Create a large fake attributeUIFields object (2.5MB of data)
    const largeFakeData = Array(2500).fill(null).map((_, i) => ({
      id: i,
      name: `Field ${i}`,
      type: 'text',
      options: Array(100).fill('option').map((opt, j) => `${opt}_${j}`),
      metadata: {
        description: 'Lorem ipsum dolor sit amet '.repeat(10),
        validation: 'Some validation rules here',
        helpText: 'Help text for this field'
      }
    }));

    // Dispatch UPDATE_SPACE_INFO with the bloated data to simulate the bug
    const currentUserData = store.getState().user.data;
    store.dispatch({
      type: 'UPDATE_SPACE_INFO',
      data: {
        ...currentUserData,
        attributeUIFields: largeFakeData
      }
    });

    devLog('✅ Large user state simulated. Now run window.__mpsCheckStoreSize() to see the size.');
    devLog('💡 Try refreshing the page - without the fix, the second refresh would log you out.');
    devLog('💡 With the fix, the userStateLimiter transform strips attributeUIFields before persistence.');
  };

  // Test helper to verify the fix is working
  window.__mpsTestUserStateFix = () => {
    devLog('🧪 Testing user state persistence fix...\n');

    // Step 1: Check current size
    const beforeState = store.getState().user;
    const beforeSize = getStateSize(beforeState);
    devLog(`1. Current user state size: ${beforeSize.mb}MB`);

    // Step 2: Simulate large state
    devLog('\n2. Adding large attributeUIFields...');
    window.__mpsSimulateLargeUserState();

    // Step 3: Check size after simulation
    const afterState = store.getState().user;
    const afterSize = getStateSize(afterState);
    devLog(`\n3. User state size after adding attributeUIFields: ${afterSize.mb}MB`);

    // Step 4: Check what would be persisted (simulate the transform)
    const userStateLimiterTransform = (state) => {
      if (state && state.data && state.data.attributeUIFields) {
        const { attributeUIFields, ...dataWithoutUIFields } = state.data;
        return {
          ...state,
          data: dataWithoutUIFields
        };
      }
      return state;
    };

    const persistedState = userStateLimiterTransform(afterState);
    const persistedSize = getStateSize(persistedState);
    devLog(`\n4. User state size that WILL BE PERSISTED (after transform): ${persistedSize.mb}MB`);

    // Step 5: Verdict
    devLog('\n📊 Results:');
    if (parseFloat(persistedSize.mb) < 0.1) {
      devLog('✅ PASS: userStateLimiter is working! attributeUIFields stripped before persistence.');
      devLog('✅ User state will persist correctly and page refreshes will work.');
    } else if (parseFloat(persistedSize.mb) > 2) {
      devLog('❌ FAIL: userStateLimiter is NOT working! User state will exceed 2MB limit.');
      devLog('❌ Second page refresh will log out the user.');
    } else {
      devLog('⚠️  WARN: Persisted size is larger than expected but under limit.');
    }

    return {
      beforeSize: beforeSize.mb,
      afterSize: afterSize.mb,
      persistedSize: persistedSize.mb,
      passed: parseFloat(persistedSize.mb) < 0.1
    };
  };
}
